import { useState } from "react";

export default function ContactForm({
  title = "Skriv til Fyns Afløbsteknik",
  subtitle = "Udfyld formularen, så vender vi hurtigt tilbage til dig.",
  className = ""
}) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    website_url: "" // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = "Angiv venligst dit navn";
    } else if (formData.name.trim().length < 2) {
      errs.name = "Navnet skal være mindst 2 tegn";
    }

    if (!formData.phone.trim()) {
      errs.phone = "Angiv venligst dit telefonnummer";
    } else if (!/^[0-9+ \-\(\)]{8,}$/.test(formData.phone.trim())) {
      errs.phone = "Indtast venligst et gyldigt telefonnummer";
    }

    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din e-mailadresse";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = "Indtast venligst en gyldig e-mailadresse";
    }

    if (!formData.message.trim()) {
      errs.message = "Beskriv venligst hvad opgaven drejer sig om";
    } else if (formData.message.trim().length < 5) {
      errs.message = "Beskrivelsen skal indeholde mindst 5 tegn";
    }

    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Hvis botten udfyldte honeypot-feltet, returner fake success uden at sende
    if (formData.website_url) {
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          navn: formData.name,
          telefon: formData.phone,
          email: formData.email,
          beskrivelse: formData.message,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setServerError(data.message || "Der opstod en fejl ved afsendelse. Ring gerne direkte på 50 49 39 50.");
      }
    } catch {
      setServerError("Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse eller ring direkte til os.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-neutral-900 text-white p-8 sm:p-10 rounded-xl border border-neutral-800 shadow-xl text-center">
        <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl border border-green-500/30">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-extrabold text-white mb-2">Tak for din henvendelse!</h3>
        <p className="text-neutral-300 max-w-md mx-auto mb-6 text-sm sm:text-base">
          Vi har modtaget din besked og kontakter dig hurtigst muligt. Har du en hastesag, kan du altid ringe direkte til os på{" "}
          <a href="tel:50493950" className="text-white font-bold underline hover:text-neutral-300 no-break">
            50 49 39 50
          </a>.
        </p>
        <button
          type="button"
          onClick={() => {
            setIsSubmitted(false);
            setFormData({ name: "", phone: "", email: "", message: "", website_url: "" });
          }}
          className="inline-flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm font-semibold px-5 py-2.5 rounded transition-colors"
        >
          <i className="fa-solid fa-rotate-left text-xs" aria-hidden="true" />
          <span>Send en ny besked</span>
        </button>
      </div>
    );
  }

  return (
    <div className={className || "bg-[#0f141c] text-white p-6 sm:p-8 md:p-10 rounded-xl border border-neutral-800 shadow-xl"}>
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight mb-2">
          {title}
        </h3>
        <p className="text-sm text-neutral-400">
          {subtitle}
        </p>
      </div>

      {serverError && (
        <div className="mb-6 p-4 rounded bg-red-950/60 border border-red-800 text-red-200 text-sm flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation mt-0.5 shrink-0 text-red-400" aria-hidden="true" />
          <div>{serverError}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Antispam honeypot: ABSOLUTELY HIDDEN */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="form-website-url">Website</label>
          <input
            type="text"
            id="form-website-url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-neutral-300 mb-1.5">
            Navn <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Dit fulde navn"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded bg-neutral-900 border text-white placeholder-neutral-500 focus:outline-none focus:ring-2 transition-colors text-sm ${
              errors.name ? "border-red-500 focus:ring-red-500" : "border-neutral-700 focus:ring-neutral-400 focus:border-neutral-400"
            }`}
          />
          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-neutral-300 mb-1.5">
              Telefonnummer <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="F.eks. 50 49 39 50"
              value={formData.phone}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded bg-neutral-900 border text-white placeholder-neutral-500 focus:outline-none focus:ring-2 transition-colors text-sm ${
                errors.phone ? "border-red-500 focus:ring-red-500" : "border-neutral-700 focus:ring-neutral-400 focus:border-neutral-400"
              }`}
            />
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-300 mb-1.5">
              E-mail <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="din@adresse.dk"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded bg-neutral-900 border text-white placeholder-neutral-500 focus:outline-none focus:ring-2 transition-colors text-sm ${
                errors.email ? "border-red-500 focus:ring-red-500" : "border-neutral-700 focus:ring-neutral-400 focus:border-neutral-400"
              }`}
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-neutral-300 mb-1.5">
            Beskrivelse / Meddelelse <span className="text-red-400">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            placeholder="Beskriv hvad du har brug for hjælp til..."
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded bg-neutral-900 border text-white placeholder-neutral-500 focus:outline-none focus:ring-2 transition-colors text-sm resize-y ${
              errors.message ? "border-red-500 focus:ring-red-500" : "border-neutral-700 focus:ring-neutral-400 focus:border-neutral-400"
            }`}
          />
          {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-white hover:bg-neutral-200 text-black font-bold py-3.5 px-6 rounded transition-colors shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
              <span>Sender besked...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane text-neutral-800" aria-hidden="true" />
              <span>Send meddelelse</span>
            </>
          )}
        </button>

        <p className="text-neutral-500 text-xs text-center mt-3">
          Vi behandler dine oplysninger fortroligt og vender tilbage hurtigst muligt.
        </p>
      </form>
    </div>
  );
}
